import {atom} from "jotai";
import {authenticatedUserAtom} from "./authenticatedUserStore";
import {routerCompanyIdAtom} from "./routerStore";
import {DEMO_COMPANY_ID} from "../utils/constants";
import {loadLastCompanyId, saveLastCompanyId} from "../utils/localStorageUtils";
import {getUserCompanies} from "../services/userService";

const lastCompanyIdPrimitiveAtom = atom<string | undefined>(loadLastCompanyId());

/**
 * Id of last selected company (persisted in Local Storage)
 */
export const lastCompanyIdAtom = atom<string | undefined, [string | undefined], void>(
    (get) => get(lastCompanyIdPrimitiveAtom),
    (_get, set, companyId) => {
        set(lastCompanyIdPrimitiveAtom, companyId);
        saveLastCompanyId(companyId ?? "");
    },
);

/**
 * Retrieving Active Company Id. The logic of companyId retrieval (ordered):
 * 1. Obtain companyId from URL, for instance /organization/{companyId}/users
 * 2. Obtain companyId from the latestCompanyId value in browser's Local Store
 * 3. Obtain companyId of the first company of the user he is a member of
 * 4. Return Demo Company Id
 */
export const activeCompanyIdAtom = atom<Promise<string>>(async (get) => {
    const routerCompanyId = get(routerCompanyIdAtom);

    if (routerCompanyId) {
        return routerCompanyId;
    }

    const lastCompanyId = get(lastCompanyIdAtom);
    const authenticatedUser = await get(authenticatedUserAtom);

    if (lastCompanyId) {
        try {
            const userCompanies = await getUserCompanies(authenticatedUser.id, {includeChildCompanies: true});
            if (userCompanies.some((company) => company.id === lastCompanyId)) {
                return lastCompanyId;
            }
        } catch (e) {
            console.error(e);
        }
    }

    if (authenticatedUser.companySecurity.length > 0) {
        return authenticatedUser.companySecurity[0].companyId;
    }

    return DEMO_COMPANY_ID;
});
